import { useCallback, useState } from 'react';

/**
 * Tracks whether a referenced `HTMLDivElement` contains any child nodes.
 *
 * Attaches a `MutationObserver` to the element via a callback ref so that
 * the returned `hasContent` flag updates automatically whenever children are
 * added or removed from the DOM — for example when a third-party widget
 * injects content into the element at runtime.
 *
 * @returns A tuple of:
 *   - `hasContent` – `true` when the element has at least one child node.
 *   - `ref` – Callback ref to attach to the target `HTMLDivElement`.
 *
 * @example
 * const [hasContent, ref] = useHasContent();
 * return <div ref={ref} className={clsx(hasContent && 'visible-class')} />;
 */
const useHasContent = (): [boolean, (node: HTMLDivElement | null) => (() => void) | undefined] => {
  const [hasContent, setHasContent] = useState(false);

  const ref = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    const checkContent = () => setHasContent(node.childNodes.length > 0);
    checkContent();

    const observer = new MutationObserver(checkContent);
    observer.observe(node, { childList: true });

    return () => observer.disconnect();
  }, []);

  return [hasContent, ref];
};

export default useHasContent;
